import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = 
process.env.NODE_ENV === 'development'
? process.env.REACT_APP_API_URL
: process.env.REACT_APP_SIDEKICK_API_URL;

console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('SIDEKICK_API_URL:', process.env.SIDEKICK_API_URL);

const SignUpForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    industry: '',
    objective: '',
    competitors: '',
    targetAudience: '',
    brandTone: '',
    sidekickRole: [],
    uploadDocs: null,
  });

  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const sidekickOptions = [
    'marketing',
    'business development',
    'design',
    'operational',
    'human resource',
    'custom role'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => {
        const newSidekickRole = checked
          ? [...prev.sidekickRole, value]
          : prev.sidekickRole.filter(role => role !== value);
        return {
          ...prev,
          sidekickRole: newSidekickRole
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const validTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!validTypes.includes(fileType)) {
        setErrors(prev => ({
          ...prev,
          uploadDocs: 'Only PDF and Excel files are allowed'
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          uploadDocs: 'File size should be less than 5MB'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        uploadDocs: file
      }));
      setUploadedFileName(file.name);
      setErrors(prev => ({
        ...prev,
        uploadDocs: null
      }));
    }
  };

  const validateStepOne = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  const validateStepTwo = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company Name is required';
    if (!formData.industry.trim()) newErrors.industry = 'Industry is required';
    if (formData.sidekickRole.length === 0) newErrors.sidekickRole = 'Please select at least one role';
    return newErrors;
  };
  

  const handleNextStep = (e) => {
    e.preventDefault();
    const newErrors = validateStepOne();
    if (Object.keys(newErrors).length === 0) {
      setStep(2);
    } else {
      setErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate step two
    const newErrors = validateStepTwo();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Log the raw form data
    console.log('Raw form data:', formData);

    // Create JSON data for submission
    const jsonData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password,
      business_name: formData.companyName.trim(),
      industry: formData.industry.trim(),
      objective: formData.objective.trim() || null,  // Make optional fields null if empty
      competitors: formData.competitors.trim() || null,
      target_audience: formData.targetAudience.trim() || null,
      brand_tone: formData.brandTone.trim() || null,
      sidekick: formData.sidekickRole.length > 0 ? formData.sidekickRole.toString() : null,
    };

    try {
      // First, try to create the user account
      const userResponse = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(jsonData),
      });

      console.log('Response status:', userResponse.status);
      console.log('Response headers:', userResponse.headers);

      if (!userResponse.ok) {
        const errorText = await userResponse.text();
        console.log('Server error response:', errorText);
        throw new Error(errorText || 'Failed to create account');
      }

      const userData = await userResponse.json();

      // If there's a file to upload, handle it separately
      if (formData.uploadDocs) {
        const fileFormData = new FormData();
        fileFormData.append('file', formData.uploadDocs);
        fileFormData.append('userId', userData.id); // Assuming the API returns a user ID

        const fileResponse = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: fileFormData,
        });

        if (!fileResponse.ok) {
          const errorText = await fileResponse.text();
          console.log('File upload error:', errorText);
          // Don't throw here, just log the error as the user account was created
        }
      }

      // Show success message
      document.getElementById("form-container").style.display = "none";
      const successMessage = document.getElementById("success-message");
      successMessage.innerHTML = `
        <p class="text-gray-700">You have successfully created your profile!</p>
        <a href="/feature" class="text-teal-600 hover:text-teal-700">Go to your dashboard</a>
      `;
      successMessage.style.display = "block";

    } catch (error) {
      console.error('Submission error:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Error creating account. Please try again.'
      }));
    }
  };


  return (
    <div className="min-h-screen bg-customColor1 flex flex-col items-center justify-center p-4">
      <Link to="/" className="block mb-8">
        <h1 className="text-center text-white text-3xl font-bold">
          SIDEKICK <span className="text-pink-500">AI</span>
        </h1>
      </Link>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-center text-[#1B1464] mb-6">
            Create Account
          </h2>

          <div id="form-container" className={`form-container ${step === 2 ? 'slide-left' : ''}`}>
            <form onSubmit={step === 1 ? handleNextStep : handleSubmit} className="space-y-4">
              {step === 1 && (
                <>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your first and last name"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      name="password"
                      placeholder="At least 6 characters"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Re-enter password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-teal-600 text-white py-2 px-4 rounded-md hover:bg-teal-700 transition-colors"
                  >
                    Continue
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Business / Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Enter your business name"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.companyName}
                      onChange={handleChange}
                    />
                    {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Industry</label>
                    <input
                      type="text"
                      name="industry"
                      placeholder="Enter your industry"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.industry}
                      onChange={handleChange}
                    />
                    {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your Main Objective</label>
                    <textarea
                      name="objective"
                      placeholder="Describe your main objective"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.objective}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Competitors</label>
                    <textarea
                      name="competitors"
                      placeholder="List your competitors"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.competitors}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Target Audience</label>
                    <textarea
                      name="targetAudience"
                      placeholder="Describe your target audience"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.targetAudience}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Brand Tone and Voice</label>
                    <textarea
                      name="brandTone"
                      placeholder="Describe your brand tone and voice"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      value={formData.brandTone}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Sidekick Role</label>
                    <div className="flex flex-wrap">
                      {sidekickOptions.map(option => (
                        <div key={option} className="mr-4 mb-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name="sidekickRole"
                              value={option}
                              checked={formData.sidekickRole.includes(option)}
                              onChange={handleChange}
                              className="form-checkbox h-4 w-4 text-teal-600"
                            />
                            <span className="ml-2 text-gray-700">{option}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Upload Docs</label>
                    <input
                      type="file"
                      name="uploadDocs"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                      onChange={handleFileChange}
                    />
                    {uploadedFileName && (
                      <p className="mt-2 text-sm text-gray-500">Uploaded: {uploadedFileName}</p>
                    )}
                    {errors.uploadDocs && <p className="text-red-500 text-xs mt-1">{errors.uploadDocs}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-teal-600 text-white py-2 px-4 rounded-md hover:bg-teal-700 transition-colors"
                  >
                    Sign Up
                  </button>
                </>
              )}

              {errors.submit && <p className="text-red-500 text-xs mt-1">{errors.submit}</p>}
            </form>
          </div>

          <div id="success-message" className="text-center mt-6" style={{ display: 'none' }}></div>

          <p className="text-center text-sm text-gray-500 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-teal-600 hover:text-teal-700">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;